/* eslint-disable camelcase */
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, Form, FormikValues } from 'formik';
import * as yup from 'yup';

import PhoneInput from 'components/PhoneInput';
import { CountryCode } from 'components/Selects/CountryCodeSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { htmlT } from 'utils/generic';

import { Container, Header, Subheader, FieldsContainer, BackButton, NextButton, ButtonsContainer } from '../../styles';
import { ErrorTag } from './styles';

export type OTPChannel = 'whatsapp' | 'sms';

export interface TelephoneValues {
  phone: string;
  country_code: CountryCode;
}

interface Props {
  values: TelephoneValues | null;
  onSubmit: (values: TelephoneValues, channel: OTPChannel) => Promise<any>;
  countryCodes: CountryCode[];
  variant?: 'incorrect-number' | 'register-number';
  hasRappiAccount: boolean;
}

const getDefaultCountry = () => {
  const { hostname } = window.location;

  if (hostname.endsWith('uy'))
    return { id: 'UY', name: 'Uruguai', culture: 'es-UY', currency: 'UYU', documentType: '' };
  if (hostname.endsWith('ar'))
    return { id: 'AR', name: 'Argentina', culture: 'es-AR', currency: 'ARS', documentType: '' };
  if (hostname.endsWith('br')) return { id: 'BR', name: 'Brasil', culture: 'pt-BR', currency: 'BRL', documentType: '' };
  if (hostname.endsWith('mx')) return { id: 'MX', name: 'Mexico', culture: 'es-MX', currency: 'MXN', documentType: '' };

  return { id: 'CO', name: 'Colombia', culture: 'es-CO', currency: 'COP', documentType: '' };
};

const Telephone = ({ values, onSubmit, countryCodes, variant, hasRappiAccount }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [otpChannel, setOtpChannel] = useState<OTPChannel>(hasRappiAccount ? 'whatsapp' : 'sms');

  const isDefaultVariant = variant === undefined;
  const isIncorrectNumberVariant = variant === 'incorrect-number';
  const isRegisterNumberVariant = variant === 'register-number';

  if (countryCodes.length === 0) return null;

  const validationSchema = yup.object().shape({
    phone: yup.string().phone().required(),
    country_code: yup.object().required(),
  });

  const initialValues: TelephoneValues = {
    phone: '',
    country_code: countryCodes.find((c) => c.id === getDefaultCountry().id) as CountryCode,
  };

  const handleBack = () => history.goBack();

  const handleSubmit = async (formValues: TelephoneValues) => {
    setLoading(true);
    await onSubmit(formValues, otpChannel);
    setLoading(false);
  };

  const getHeaderContent = () => {
    if (isRegisterNumberVariant) return t('invite.telephone.registerNumber.title');
    return t('invite.telephone.title');
  };
  const getSubheaderContent = () => {
    if (isIncorrectNumberVariant) {
      if (values)
        return htmlT(t, 'invite.telephone.incorrectNumber.subtitle', {
          country_code: values.country_code.code,
          number: values.phone,
        });
      return t('invite.telephone.incorrectNumber.subtitleNoPhone');
    }
    if (isRegisterNumberVariant) return t('invite.telephone.registerNumber.subtitle');
    return t('invite.telephone.subtitle');
  };

  return (
    <Formik
      initialStatus={{ rightCheckIfIsValid: true }}
      initialValues={isDefaultVariant ? values || initialValues : initialValues}
      isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, submitForm }: FormikValues) => (
        <Form>
          <Container>
            <Header>{getHeaderContent()}</Header>
            {isIncorrectNumberVariant && <ErrorTag>{t('invite.telephone.incorrectNumber.tag')}</ErrorTag>}
            <Subheader>{getSubheaderContent()}</Subheader>

            <FieldsContainer>
              <PhoneInput
                phoneField="phone"
                countryCodeField="country_code"
                countryCodes={countryCodes}
                mask={'9'.repeat(50)}
              />
            </FieldsContainer>

            <ButtonsContainer>
              <BackButton type="button" onClick={handleBack}>
                {t('invite.backButton')}
              </BackButton>

              {hasRappiAccount ? (
                <>
                  <NextButton
                    type="button"
                    disabled={!isValid || loading}
                    loading={loading && otpChannel === 'sms'}
                    onClick={() => {
                      setOtpChannel('sms');
                      submitForm();
                    }}
                  >
                    {t('invite.telephone.sendCode.sms')}
                  </NextButton>
                  <NextButton
                    type="button"
                    disabled={!isValid || loading}
                    loading={loading && otpChannel === 'whatsapp'}
                    onClick={() => {
                      setOtpChannel('whatsapp');
                      submitForm();
                    }}
                  >
                    {t('invite.telephone.sendCode.whatsapp')}
                  </NextButton>
                </>
              ) : (
                <NextButton
                  type="button"
                  disabled={!isValid || loading}
                  loading={loading && otpChannel === 'sms'}
                  onClick={() => {
                    setOtpChannel('sms');
                    submitForm();
                  }}
                >
                  {t('invite.telephone.continue')}
                </NextButton>
              )}
            </ButtonsContainer>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

export default Telephone;
